import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { userApi, ayurvedicApi, humanPsychologyApi } from '../../config';
import { getPillarMeta, getSpecialityLabel } from '../../utils/breadcrumb';
import categoryConfigData from './trainersCategoryConfig.json';

interface Trainer {
  id: string;
  mentorId: string;
  name: string;
  role: string;
  city: string;
  rating: string;
  price: string;
  image?: string;
  color?: string;
  initials?: string;
  slug?: string;
}

interface TrainerCategory {
  category: string;
  trainers: Trainer[];
}

const TRAINER_COLORS = [
  'var(--forest)',
  'var(--marigold-dark)',
  'var(--indigo)',
  'var(--forest-dark)',
  'var(--rose)'
];

const FALLBACK_CATEGORIES: TrainerCategory[] = [
  {
    category: "Yoga Practitioners",
    trainers: [
      { id: "mock1", mentorId: "m1", name: "Meenakshi Paliwal", role: "Yoga Instructor", city: "Online", rating: "4.9", price: "₹499", color: "var(--forest)", initials: "MP" },
      { id: "mock2", mentorId: "m2", name: "Rohit Sharma", role: "Yoga Instructor", city: "Online", rating: "4.8", price: "₹499", color: "var(--forest-dark)", initials: "RS" },
      { id: "mock3", mentorId: "m3", name: "Unnati Rungta", role: "Yoga Instructor", city: "Online", rating: "4.7", price: "₹499", color: "var(--indigo)", initials: "UR" },
      { id: "mock4", mentorId: "m4", name: "Nia Singh", role: "Yoga Instructor", city: "Online", rating: "4.8", price: "₹499", color: "var(--forest)", initials: "NS" },
      { id: "mock5", mentorId: "m5", name: "Aditya Shrivastava", role: "Yoga Instructor", city: "Online", rating: "4.9", price: "₹499", color: "var(--marigold-dark)", initials: "AS" },
      {
        id: "mock6",
        mentorId: "fdf19238-e174-40aa-8223-49d0a34d136f",
        name: "Prachi Vijayvargiya",
        role: "Energy Healing Consultant",
        city: "Online",
        rating: "4.9",
        price: "₹499",
        image: "https://res.cloudinary.com/dfppeybmu/image/upload/v1783753371/vendor_profiles/v2yif0b6dxksgi4okwxc.jpg",
        color: "var(--rose)",
        initials: "PV",
      }
    ]
  },
  {
    category: "Doctors",
    trainers: [
      { id: "doc1", mentorId: "m7", name: "Dr. Chandra Choudhry", role: "Ayurvedic Doctor", city: "Online", rating: "4.8", price: "₹499", color: "var(--rose)", initials: "CC" },
      { id: "doc2", mentorId: "m8", name: "Dr. Dheeraj Meghwal", role: "Ayurvedic Doctor", city: "Online", rating: "4.6", price: "₹499", color: "var(--forest)", initials: "DM" },
      { id: "doc3", mentorId: "m9", name: "Dr. Ananya Sen", role: "Ayurvedic Specialist", city: "Online", rating: "4.9", price: "₹499", color: "var(--marigold-dark)", initials: "AS" },
      { id: "doc4", mentorId: "m10", name: "Dr. Ramesh Sharma", role: "Naturopathy & Ayurveda", city: "Online", rating: "4.8", price: "₹499", color: "var(--forest-dark)", initials: "RS" }
    ]
  },
  {
    category: "Wellness Experts",
    trainers: [
      { id: "well1", mentorId: "m9", name: "Graham Travis", role: "Wellness Coach", city: "Online", rating: "4.9", price: "₹499", color: "var(--marigold-dark)", initials: "GT" },
      { id: "well2", mentorId: "m10", name: "Pushpa Singh", role: "Fitness Trainer", city: "Online", rating: "4.8", price: "₹499", color: "var(--indigo)", initials: "PS" }
    ]
  }
];

const categoryConfig: Record<string, any> = categoryConfigData;

// Helper to cleanly format locations from strings, JSON strings, or objects
const cleanPrice = (p?: string): string => {
  if (!p) return '₹499';
  return p.replace(/\s*\/\s*session/gi, '').trim();
};

const formatLocation = (rawLocation: any, rawCity?: string): string => {
  const tryExtractFromObj = (obj: any): string | null => {
    if (!obj || typeof obj !== 'object') return null;
    if (obj.city && typeof obj.city === 'string' && obj.city.trim() !== '') return obj.city.trim();
    if (obj.state && typeof obj.state === 'string' && obj.state.trim() !== '') return obj.state.trim();
    if (obj.address && typeof obj.address === 'string' && obj.address.trim() !== '') {
      const addr = obj.address.trim();
      if (!addr.startsWith('{') && !addr.startsWith('[')) return addr;
    }
    return null;
  };

  // 1. Check rawCity if valid and not JSON
  if (rawCity && typeof rawCity === 'string') {
    const trimmed = rawCity.trim();
    if (trimmed && !trimmed.startsWith('{') && !trimmed.startsWith('[') && trimmed !== '[object Object]') {
      return trimmed;
    }
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const parsed = JSON.parse(trimmed);
        const extracted = tryExtractFromObj(parsed);
        if (extracted) return extracted;
      } catch (e) {}
    }
  }

  // 2. If rawLocation is an object
  if (rawLocation && typeof rawLocation === 'object') {
    const extracted = tryExtractFromObj(rawLocation);
    if (extracted) return extracted;
  }

  // 3. If rawLocation is a string
  if (rawLocation && typeof rawLocation === 'string') {
    const trimmed = rawLocation.trim();
    if (!trimmed || trimmed === '[object Object]') return 'Online';
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const parsed = JSON.parse(trimmed);
        const extracted = tryExtractFromObj(parsed);
        if (extracted) return extracted;
        return 'Online';
      } catch (e) {
        return 'Online';
      }
    }
    return trimmed;
  }

  return 'Online';
};

const TrainerCategoryRow: React.FC<{ categoryName: string, trainers: Trainer[] }> = ({ categoryName, trainers }) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const scrollLeft = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: -240, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: 240, behavior: 'smooth' });
    }
  };

  if (!trainers || trainers.length === 0) return null;

  const config = categoryConfig[categoryName] || categoryConfig["default"];
  const displayTitle = config.title;

  const isYogaAndHealers = categoryName === 'Yoga Practitioners';
  const isDoctors = categoryName === 'Doctors';
  const isWellness = categoryName === 'Wellness Experts';

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.6 }}
      className="mb-6 last:mb-0 relative bg-white"
      style={isYogaAndHealers ? {
        backgroundColor: '#FFFFFF',
        paddingTop: '24px',
        paddingBottom: '14px',
        marginBottom: 0,
        borderTop: '1px solid rgba(21,48,43,0.05)',
        borderBottom: '1px solid rgba(21,48,43,0.05)',
      } : isDoctors ? {
        backgroundColor: '#FFFFFF',
        paddingTop: '14px',
        paddingBottom: '18px',
        marginBottom: 0,
        borderBottom: '1px solid rgba(21,48,43,0.05)',
      } : isWellness ? {
        backgroundColor: '#FFFFFF',
        paddingTop: '24px',
        paddingBottom: '18px',
        marginBottom: 0,
      } : { backgroundColor: '#FFFFFF' }}
    >
      <div className="max-w-[1240px] mx-auto px-5 sm:px-8 flex items-center justify-between mb-3 gap-3">
        <div>
          <h3 className="font-bold tracking-tight text-[16px] sm:text-[18px] text-slate-900 leading-tight m-0">
            {displayTitle}
          </h3>
          {config.subtitle && (
            <p className="text-slate-500 text-[12px] sm:text-[13px] mt-0.5 max-w-xl font-normal hidden sm:block">
              {config.subtitle}
            </p>
          )}
        </div>
        <button 
          onClick={() => navigate(config.link || '/services')}
          className="inline-flex items-center gap-1.5 text-[13px] sm:text-[14px] font-semibold text-slate-900 hover:text-[#1E443B] transition-colors cursor-pointer shrink-0 whitespace-nowrap bg-transparent border-0 p-0 shadow-none outline-none"
        >
          View All <ArrowRight size={15} strokeWidth={2.2} />
        </button>
      </div>

      <div className="max-w-[1240px] mx-auto px-5 sm:px-8 relative group">
        {/* Left Arrow */}
        <button 
          onClick={scrollLeft}
          className="absolute left-0 top-1/2 -translate-y-1/2 -ml-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-white text-[#15302B] shadow-[0_4px_16px_rgb(0,0,0,0.12)] opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 hover:bg-[#15302B] hover:text-white hidden lg:flex disabled:opacity-0 focus:outline-none border border-gray-100"
          aria-label="Scroll left"
        >
          <ChevronLeft size={18} strokeWidth={2} />
        </button>

        {/* Slider Container */}
        <div 
          ref={sliderRef}
          className="flex gap-3.5 sm:gap-4 overflow-x-auto pb-4 pt-1 snap-x snap-mandatory hide-scrollbar scroll-smooth mx-[-20px] px-[20px] md:mx-0 md:px-0"
        >
          {trainers.map((t) => (
            <div
              key={t.id}
              className="group/card flex-none w-[205px] sm:w-[220px] snap-start bg-white border border-[#15302B]/[0.08] rounded-[18px] overflow-hidden shadow-[0_2px_10px_-4px_rgba(21,48,43,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_24px_-8px_rgba(21,48,43,0.12)] cursor-pointer relative flex flex-col"
              onClick={() => navigate(`/public/instructor-profile/${t.slug || t.mentorId || t.id}`, {
                state: {
                  sourcePillar: getPillarMeta(t.role),
                  sourceSpeciality: getSpecialityLabel(t.role),
                  sourceName: t.name,
                },
              })}
            >
              <div className="h-[155px] sm:h-[165px] w-full relative overflow-hidden flex items-center justify-center font-serif text-[44px] text-white font-medium bg-[#F4F6F5]">
                {/* Subtle ambient blurred backdrop for depth */}
                {t.image && (
                  <img 
                    src={t.image} 
                    alt="" 
                    aria-hidden="true"
                    className="absolute inset-0 w-full h-full object-cover blur-md opacity-25 scale-110 pointer-events-none" 
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                )}
                {t.image ? (
                  <img 
                    src={t.image} 
                    alt={t.name} 
                    className="relative z-10 w-full h-full object-contain p-1.5 transition-transform duration-500 group-hover/card:scale-105" 
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                      const fallback = (e.target as HTMLElement).parentElement?.querySelector('.trainer-initials-fallback');
                      if (fallback) (fallback as HTMLElement).style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  className="trainer-initials-fallback absolute inset-0 w-full h-full flex items-center justify-center font-serif text-[44px] text-white font-medium"
                  style={{
                    background: t.color || 'var(--forest)',
                    display: t.image ? 'none' : 'flex'
                  }}
                >
                  <span className="transition-transform duration-500 group-hover/card:scale-110 inline-block drop-shadow-md">
                    {t.initials}
                  </span>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#15302B]/20 via-transparent to-transparent opacity-0 group-hover/card:opacity-100 transition-opacity duration-300 pointer-events-none z-20"></div>
              </div>
              
              <div className="p-3 sm:p-3.5 flex flex-col flex-grow bg-white relative z-10">
                <div className="mb-1">
                  <h4 className="trainer-card-name font-semibold tracking-tight text-[13px] sm:text-[13.5px] leading-snug text-[#15302B] group-hover/card:text-[#2C4A3B] transition-colors truncate">
                    {t.name}
                  </h4>
                </div>
                
                <div className="flex justify-between items-center text-[10.5px] sm:text-[11px] text-[#556963] mt-1 mb-2.5">
                  <span className="flex items-center gap-1.5 bg-[#F5F7F6] px-2 py-0.5 rounded-full">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                    <span className="truncate max-w-[95px]">{formatLocation(t.city)}</span>
                  </span>
                  <span className="flex items-center gap-1 font-medium px-1.5 py-0.5 bg-[#FFF9E6] text-[#B87C17] rounded-full shadow-2xs">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                    {t.rating || '4.9'}
                  </span>
                </div>
                
                <div className="flex justify-between items-center border-t border-gray-100 pt-2 mt-auto">
                  <div className="flex flex-col">
                    <span className="text-[9px] uppercase tracking-wider text-[#7B8D88] font-semibold leading-none mb-0.5">Session</span>
                    <span className="font-mono text-[13px] sm:text-[13.5px] font-bold text-[#15302B] leading-none">{cleanPrice(t.price)}</span>
                  </div>
                  <span className="text-[11.5px] font-medium px-3.5 py-1.5 rounded-full bg-[#15302B] text-white transition-all duration-300 group-hover/card:bg-[#2C4A3B] group-hover/card:shadow-md hover:scale-105 active:scale-95">
                    Book
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Right Arrow */}
        <button 
          onClick={scrollRight}
          className="absolute right-0 top-1/2 -translate-y-1/2 -mr-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-white text-[#15302B] shadow-[0_4px_16px_rgb(0,0,0,0.12)] opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 hover:bg-[#15302B] hover:text-white hidden lg:flex disabled:opacity-0 focus:outline-none border border-gray-100"
          aria-label="Scroll right"
        >
          <ChevronRight size={18} strokeWidth={2} />
        </button>
      </div>
    </motion.div>
  );
};

const TrainersSection: React.FC = () => {
  const [categories, setCategories] = useState<TrainerCategory[]>(FALLBACK_CATEGORIES);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;

    const fetchTrainers = async () => {
      try {
        const [instructorsRes, ayurvedaRes, psychologyRes, trainersRes] = await Promise.allSettled([
          userApi.get<any>('/view-homepage-instructors'),
          ayurvedicApi.get<any>('/ayurveda/get-all-doctors'),
          humanPsychologyApi.get<any>('/Psychology/get-all-doctors'),
          userApi.get<any>('/homepage-trainers')
        ]);

        const collectedTrainers: any[] = [];

        // 1. Ingest from /view-homepage-instructors (Yoga & Healers)
        if (instructorsRes.status === 'fulfilled' && instructorsRes.value.data) {
          const resData = instructorsRes.value.data;
          const list = Array.isArray(resData) ? resData : resData.instructors || resData.data || [];
          list.forEach((t: any) => {
            const fullName = `${t.firstname || t.firstName || t.name || ''} ${t.lastname || t.lastName || ''}`.trim() || t.name;
            if (fullName) {
              collectedTrainers.push({
                id: t._id || t.id,
                mentorId: t.mentorId || t._id,
                name: fullName,
                role: t.role || t.subRole || 'Yoga Instructor',
                city: formatLocation(t.location, t.city),
                rating: String(t.rating || '4.8'),
                price: t.hourlyRate ? `₹${t.hourlyRate}` : (t.price || '₹499'),
                image: t.profileIcon || t.image,
                slug: t.slug || t.mentorId || t._id
              });
            }
          });
        }

        // 2. Ingest from /ayurveda/get-all-doctors (All Ayurvedic Doctors)
        if (ayurvedaRes.status === 'fulfilled' && ayurvedaRes.value.data) {
          const resData = ayurvedaRes.value.data;
          const list = Array.isArray(resData) ? resData : resData.data || resData.doctors || [];
          list.forEach((doc: any) => {
            const fullName = `${doc.firstname || doc.firstName || doc.name || ''} ${doc.lastname || doc.lastName || ''}`.trim() || doc.name;
            if (fullName) {
              collectedTrainers.push({
                id: doc._id || doc.id || doc.doctorId,
                mentorId: doc.mentorId || doc.doctorId || doc._id,
                name: fullName,
                role: doc.role || (Array.isArray(doc.specializations) ? doc.specializations[0] : doc.specializations) || 'Ayurvedic Doctor',
                city: formatLocation(doc.location, doc.city),
                rating: String(doc.rating || '4.8'),
                price: doc.hourlyRate ? `₹${doc.hourlyRate}` : (doc.price || '₹499'),
                image: doc.profileIcon || doc.image,
                slug: doc.slug || doc.mentorId || doc.doctorId || doc._id
              });
            }
          });
        }

        // 3. Ingest from /Psychology/get-all-doctors (Psychologists & Therapists)
        if (psychologyRes.status === 'fulfilled' && psychologyRes.value.data) {
          const resData = psychologyRes.value.data;
          const list = Array.isArray(resData) ? resData : resData.data || resData.doctors || [];
          list.forEach((psy: any) => {
            const fullName = `${psy.firstname || psy.firstName || psy.name || ''} ${psy.lastname || psy.lastName || ''}`.trim() || psy.name;
            if (fullName) {
              collectedTrainers.push({
                id: psy._id || psy.id || psy.mentorId,
                mentorId: psy.mentorId || psy._id,
                name: fullName,
                role: psy.role || (Array.isArray(psy.specializations) ? psy.specializations[0] : psy.specializations) || 'Psychology Specialist',
                city: formatLocation(psy.location, psy.city),
                rating: String(psy.rating || '4.8'),
                price: psy.hourlyRate ? `₹${psy.hourlyRate}` : (psy.price || '₹499'),
                image: psy.profileIcon || psy.image,
                slug: psy.slug || psy.mentorId || psy._id
              });
            }
          });
        }

        // 4. Ingest from /homepage-trainers (Platform Mentors)
        if (trainersRes.status === 'fulfilled' && trainersRes.value.data) {
          const resData = trainersRes.value.data;
          const list = Array.isArray(resData) ? resData : resData.data || resData.trainers || [];
          list.forEach((item: any) => {
            if (item.trainers && Array.isArray(item.trainers)) {
              item.trainers.forEach((t: any) => {
                collectedTrainers.push({
                  ...t,
                  city: formatLocation(t.location, t.city)
                });
              });
            } else if (item.name) {
              collectedTrainers.push({
                ...item,
                city: formatLocation(item.location, item.city)
              });
            }
          });
        }

        // Deduplicate
        const seen = new Set<string>();
        const uniqueTrainers: Trainer[] = [];
        collectedTrainers.forEach((t, index) => {
          const roleLower = String(t.role || '').toLowerCase();
          if (roleLower === 'admin' || roleLower.includes('admin')) {
            return;
          }
          const key = String(t.mentorId || t.slug || t.id || t.name).toLowerCase();
          if (!seen.has(key)) {
            seen.add(key);
            let initials = '?';
            if (t.name) {
              const names = String(t.name).trim().split(' ').filter((n: string) => n.length > 0);
              if (names.length > 0) {
                initials = names[0][0];
                if (names.length > 1) initials += names[names.length - 1][0];
              }
            }
            uniqueTrainers.push({
              id: t.id || t._id || `trainer-${index}`,
              mentorId: t.mentorId || t._id || `m-${index}`,
              name: t.name,
              role: t.role || 'Yoga Instructor',
              city: formatLocation(t.city || t.location),
              rating: String(t.rating || '4.8'),
              price: t.price || '₹499',
              image: t.image || t.profileIcon,
              slug: t.slug,
              initials: initials.toUpperCase(),
              color: TRAINER_COLORS[index % TRAINER_COLORS.length]
            });
          }
        });

        let finalCategories: TrainerCategory[] = [];

        if (uniqueTrainers.length > 0) {
          const yogaAndHealers = uniqueTrainers.filter(t => {
            const r = (t.role || '').toLowerCase();
            return r.includes('yoga') || r.includes('heal') || r.includes('meditat') || r.includes('pranayama') || r.includes('sound') || r.includes('reiki') || r.includes('breath');
          });

          const doctors = uniqueTrainers.filter(t => {
            const r = (t.role || '').toLowerCase();
            return r.includes('ayurved') || r.includes('doctor') || r.includes('naturopath') || r.includes('dr.') || r.includes('consult');
          });

          const wellness = uniqueTrainers.filter(t => {
            const r = (t.role || '').toLowerCase();
            if (r === 'admin' || r.includes('admin')) return false;
            return !yogaAndHealers.includes(t) && !doctors.includes(t);
          });

          // Sort wellness experts in the exact consistent order requested
          const PREFERRED_WELLNESS_ORDER = [
            'meitrai mehta',
            'pushpa singh',
            'graham travis',
            'hemant paliwal'
          ];

          wellness.sort((a, b) => {
            const nameA = (a.name || '').toLowerCase().trim();
            const nameB = (b.name || '').toLowerCase().trim();
            const indexA = PREFERRED_WELLNESS_ORDER.indexOf(nameA);
            const indexB = PREFERRED_WELLNESS_ORDER.indexOf(nameB);

            if (indexA !== -1 && indexB !== -1) return indexA - indexB;
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
            return nameA.localeCompare(nameB);
          });

          if (yogaAndHealers.length > 0) {
            const fallbackYoga = FALLBACK_CATEGORIES.find(c => c.category === 'Yoga Practitioners')?.trainers || [];
            const existingNames = new Set(yogaAndHealers.map(d => d.name.toLowerCase()));
            const mergedYoga = [...yogaAndHealers];
            fallbackYoga.forEach(fb => {
              if (!existingNames.has(fb.name.toLowerCase())) {
                mergedYoga.push(fb);
              }
            });
            finalCategories.push({ category: 'Yoga Practitioners', trainers: mergedYoga });
          }

          if (doctors.length > 0) {
            const fallbackDocs = FALLBACK_CATEGORIES.find(c => c.category === 'Doctors')?.trainers || [];
            const existingNames = new Set(doctors.map(d => d.name.toLowerCase()));
            const mergedDocs = [...doctors];
            fallbackDocs.forEach(fb => {
              if (!existingNames.has(fb.name.toLowerCase())) {
                mergedDocs.push(fb);
              }
            });
            finalCategories.push({ category: 'Doctors', trainers: mergedDocs });
          }

          if (wellness.length > 0) {
            finalCategories.push({ category: 'Wellness Experts', trainers: wellness });
          }
        }

        if (finalCategories.length === 0 || finalCategories.every(c => c.trainers.length === 0)) {
          finalCategories = FALLBACK_CATEGORIES;
        }

        if (isMounted) {
          setCategories(finalCategories);
        }
      } catch (error) {
        console.error('Error fetching homepage trainers:', error);
        if (isMounted) {
          setCategories(FALLBACK_CATEGORIES);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTrainers();

    return () => {
      isMounted = false;
    };
  }, []);

  if (loading) {
    return (
      <section className="py-20 bg-[#FFFFFF]" id="trainers" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-[#2C4A3B]/20 border-t-[#2C4A3B] rounded-full animate-spin"></div>
          <p className="text-[#3B5750] font-medium tracking-wide">Curating practitioners...</p>
        </div>
      </section>
    );
  }

  if (categories.length === 0) {
    return null;
  }

  return (
    <section className="py-6 bg-[#FFFFFF] overflow-hidden" id="trainers">
      <div className="flex flex-col space-y-0">
        {categories.map((cat, index) => (
          <TrainerCategoryRow key={index} categoryName={cat.category} trainers={cat.trainers} />
        ))}
      </div>
    </section>
  );
};

export default TrainersSection;
